import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, isBefore, addMonths, addYears } from 'date-fns';

const { FiPlus, FiRefreshCw, FiTrash2, FiClock, FiCheckCircle, FiX, FiCheck, FiDollarSign, FiTrendingUp } = FiIcons;

// ─── Revenue Schedule Modal ─────────────────────────────────────────
const RecurringRevenueModal = ({ isOpen, onClose, onSave, properties, categories, tags, initialData }) => {
  const defaults = {
    propertyId: '',
    amount: '',
    category: categories[0] || 'Rent',
    description: '',
    frequency: 'Monthly',
    nextDueDate: format(new Date(), 'yyyy-MM-dd'),
    tags: ['Automatic'],
    status: 'Active'
  };
  const [formData, setFormData] = useState(initialData ? { ...defaults, ...initialData } : defaults);

  React.useEffect(() => {
    setFormData(initialData ? { ...defaults, ...initialData } : defaults);
  }, [initialData, isOpen]);

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-green-100 text-green-600 rounded-lg">
              <SafeIcon icon={FiTrendingUp} size={16} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{initialData?.id ? 'Edit Revenue Schedule' : 'New Revenue Schedule'}</h2>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Recurring Income</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Property</label>
              <select value={formData.propertyId} onChange={e => setFormData({ ...formData, propertyId: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none">
                <option value="">General Portfolio</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Amount ($) *</label>
              <input type="number" min="0" step="0.01" required value={formData.amount}
                onChange={e => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none" placeholder="0.00" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
              <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Frequency</label>
              <select value={formData.frequency} onChange={e => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none">
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Next Collection Date</label>
            <input type="date" value={formData.nextDueDate} onChange={e => setFormData({ ...formData, nextDueDate: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Description *</label>
            <textarea rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none resize-none"
              placeholder="e.g. Monthly rent collection, Pet fee, Parking income..." />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {tags.map(tag => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase border transition-all ${
                    formData.tags.includes(tag) ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-400 border-gray-100 hover:border-green-200'
                  }`}>
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
          <button onClick={() => { onSave({ ...formData, amount: Number(formData.amount) || 0 }); onClose(); }}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
            <SafeIcon icon={FiCheck} size={15} /> Save Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Revenue Manager Component ───────────────────────────────────────
const RecurringRevenueManager = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const schedules = data.recurringRevenue || [];
  const today = new Date();

  const handlePostTransaction = (schedule) => {
    addRecord('transactions', {
      propertyId: schedule.propertyId,
      date: format(today, 'yyyy-MM-dd'),
      type: 'Income',
      category: schedule.category,
      amount: schedule.amount,
      description: `[Auto] ${schedule.description}`,
      tags: [...(schedule.tags || []), 'Recurring']
    });

    let nextDate = parseISO(schedule.nextDueDate);
    if (schedule.frequency === 'Monthly') nextDate = addMonths(nextDate, 1);
    else if (schedule.frequency === 'Quarterly') nextDate = addMonths(nextDate, 3);
    else if (schedule.frequency === 'Annually') nextDate = addYears(nextDate, 1);

    updateRecord('recurringRevenue', schedule.id, {
      nextDueDate: format(nextDate, 'yyyy-MM-dd')
    });
  };

  const monthlyTotal = schedules.reduce((sum, s) => {
    const amt = Number(s.amount) || 0;
    if (s.frequency === 'Monthly') return sum + amt;
    if (s.frequency === 'Quarterly') return sum + amt / 3;
    if (s.frequency === 'Annually') return sum + amt / 12;
    return sum;
  }, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-xl font-black text-gray-900">Revenue Automation</h3>
          <p className="text-xs text-gray-500 font-medium">Manage recurring income streams and collections.</p>
        </div>
        <button onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
          className="flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition font-black text-[10px] uppercase tracking-widest">
          <SafeIcon icon={FiPlus} className="mr-2" /> New Schedule
        </button>
      </div>

      {/* Summary */}
      {schedules.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} size={18} />
            </div>
            <div>
              <p className="text-sm font-bold text-green-900">Projected Monthly Revenue</p>
              <p className="text-xs text-green-600">{schedules.length} active schedule{schedules.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <p className="text-2xl font-black text-green-700">${Math.round(monthlyTotal).toLocaleString()}/mo</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {schedules.map(s => {
          const isDue = isBefore(parseISO(s.nextDueDate), today);
          const property = data.properties.find(p => p.id === s.propertyId);

          return (
            <div key={s.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDue ? 'bg-amber-100 text-amber-600' : 'bg-green-50 text-green-600'}`}>
                  <SafeIcon icon={FiRefreshCw} size={18} className={isDue ? 'animate-spin' : ''} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{s.description}</h4>
                  <p className="text-[10px] text-gray-400 font-semibold">
                    {property?.name || 'General Portfolio'} &middot; {s.category} &middot; {s.frequency}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 px-2">
                {(s.tags || []).filter(t => t !== 'Automatic').map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-gray-50 text-gray-400 rounded text-[9px] font-bold uppercase">#{tag}</span>
                ))}
              </div>

              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-lg font-black text-green-600">${Number(s.amount).toLocaleString()}</p>
                  <p className={`text-[10px] font-semibold ${isDue ? 'text-amber-600' : 'text-gray-400'}`}>
                    Next: {format(parseISO(s.nextDueDate), 'MMM dd, yyyy')}
                  </p>
                </div>

                <div className="flex gap-1.5">
                  {isDue && (
                    <button onClick={() => handlePostTransaction(s)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg font-bold text-[10px] uppercase hover:bg-green-700 transition flex items-center gap-1.5">
                      <SafeIcon icon={FiCheckCircle} size={13} /> Collect
                    </button>
                  )}
                  <button onClick={() => { setEditingItem(s); setIsModalOpen(true); }}
                    className="p-2 text-gray-300 hover:text-indigo-500 transition-colors">
                    <SafeIcon icon={FiIcons.FiEdit2} size={14} />
                  </button>
                  <button onClick={() => deleteRecord('recurringRevenue', s.id)}
                    className="p-2 text-gray-300 hover:text-red-500 transition-colors">
                    <SafeIcon icon={FiTrash2} size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {schedules.length === 0 && (
          <div className="py-16 text-center border-2 border-dashed border-gray-100 rounded-2xl bg-white">
            <SafeIcon icon={FiClock} size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No recurring revenue scheduled</p>
            <p className="text-[10px] text-gray-300 mt-1">Create schedules for rent, pet fees, parking, and other recurring income</p>
          </div>
        )}
      </div>

      <RecurringRevenueModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
        onSave={(item) => editingItem ? updateRecord('recurringRevenue', editingItem.id, item) : addRecord('recurringRevenue', item)}
        properties={data.properties}
        categories={data.incomeCategories || ['Rent', 'Pet Fee', 'Cleaning Fee']}
        tags={data.transactionTags || []}
        initialData={editingItem}
      />
    </div>
  );
};

export default RecurringRevenueManager;
